/**
 * Redeeming a token exactly once, no matter how often the view mounts.
 *
 * Both token surfaces POST the token from an effect. React StrictMode runs
 * effects twice on mount, and a remount (locale switch, fast refresh) would run
 * them again — a magic link is single-use, so a second POST would burn it and
 * greet the reader with "bereits verwendet" on their very first click.
 *
 * The promise is therefore keyed by the token at module scope: the second call
 * gets the first call's promise, not a second request. A network failure (status
 * 0) is dropped from the cache so it stays retryable; a real backend answer
 * ("abgelaufen", "widerrufen") is cached, because retrying cannot change it.
 */
import { ApiError, api } from '@/api/client'
import type { SessionInfo } from '@/api/types'

export type TokenKind = 'magic-link' | 'invite'

const inFlight = new Map<string, Promise<SessionInfo>>()

export function redeemOnce(kind: TokenKind, token: string): Promise<SessionInfo> {
  const key = `${kind}:${token}`
  const existing = inFlight.get(key)
  if (existing) return existing

  const started = (kind === 'magic-link' ? api.consumeMagicLink(token) : api.acceptInvite(token))
    .catch((cause: unknown) => {
      if (cause instanceof ApiError && cause.status === 0) inFlight.delete(key)
      throw cause
    })
  inFlight.set(key, started)
  return started
}

/** Test seam only — the cache is module state and must not leak across cases. */
export function resetRedeemCache(): void {
  inFlight.clear()
}
